/**
 * Audio Controller
 * Manages audio playback (WAV files or video audio)
 */

import { saveVideo, getVideo } from '../utils/indexeddb.js';

/**
 * Audio Controller class for audio playback management
 */
export class AudioController {
  constructor() {
    this.audioElement = null;
    this.currentAudioId = null;
    this.isPlaying = false;
    this.objectURL = null;
    this.audioType = null; // 'wav' or 'video'
  }

  /**
   * Initialize audio controller
   */
  init() {
    this.audioElement = new Audio();
    this.audioElement.loop = true;
    this.audioElement.muted = true; // Start muted for autoplay
    console.log('AudioController initialized');
  }

  /**
   * Load audio from URL with IndexedDB caching
   * @param {string} audioId - Audio ID for caching
   * @param {string} audioUrl - URL to audio file
   * @param {string} type - Audio type: 'wav' or 'video'
   * @param {Function} onProgress - Optional progress callback
   * @returns {Promise<void>}
   */
  async load(audioId, audioUrl, type = 'wav', onProgress = null) {
    try {
      console.log(`Loading audio ${audioId} from ${audioUrl} (${type})`);
      this.audioType = type;

      // Check IndexedDB cache first
      const cachedAudio = await getVideo(audioId); // Reuse video cache for audio

      let blob;
      if (cachedAudio && cachedAudio.blob) {
        console.log(`Audio ${audioId} loaded from IndexedDB cache`);
        blob = cachedAudio.blob;
        if (onProgress) onProgress(100);
      } else {
        // Download audio with progress tracking
        console.log(`Downloading audio ${audioId}...`);
        const response = await fetch(audioUrl);

        if (!response.ok) {
          throw new Error(`Failed to download audio: ${response.status}`);
        }

        const contentLength = response.headers.get('content-length');
        if (!contentLength || !onProgress) {
          blob = await response.blob();
        } else {
          // Track download progress
          const total = parseInt(contentLength, 10);
          let loaded = 0;

          const reader = response.body.getReader();
          const chunks = [];

          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            chunks.push(value);
            loaded += value.length;

            if (onProgress) {
              const percent = (loaded / total) * 100;
              onProgress(percent);
            }
          }

          blob = new Blob(chunks);
        }

        console.log(`Audio ${audioId} downloaded (${blob.size} bytes)`);

        // Cache in IndexedDB
        saveVideo(audioId, blob, { url: audioUrl, cachedAt: Date.now(), type }).catch((error) => {
          console.warn('Failed to cache audio in IndexedDB:', error);
        });
      }

      // Revoke previous object URL if exists
      if (this.objectURL) {
        URL.revokeObjectURL(this.objectURL);
      }

      // Create object URL for audio
      this.objectURL = URL.createObjectURL(blob);
      this.audioElement.src = this.objectURL;

      // Wait for audio to be ready
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Audio load timeout after 30 seconds'));
        }, 30000);

        this.audioElement.onloadeddata = () => {
          clearTimeout(timeout);
          console.log(`Audio ${audioId} ready to play`);
          resolve();
        };

        this.audioElement.onerror = () => {
          clearTimeout(timeout);
          reject(new Error('Failed to load audio element'));
        };

        this.audioElement.onloadedmetadata = () => {
          clearTimeout(timeout);
          console.log(`Audio ${audioId} metadata loaded`);
          resolve();
        };
      });

      this.currentAudioId = audioId;
    } catch (error) {
      console.error(`Failed to load audio ${audioId}:`, error);
      throw error;
    }
  }

  /**
   * Play audio
   * @returns {Promise<void>}
   */
  async play() {
    if (!this.audioElement) {
      throw new Error('AudioController not initialized');
    }

    try {
      await this.audioElement.play();
      this.isPlaying = true;
      console.log('Audio playback started');
    } catch (error) {
      console.error('Failed to play audio:', error);
      throw error;
    }
  }

  /**
   * Pause audio
   */
  pause() {
    if (!this.audioElement) {
      return;
    }

    this.audioElement.pause();
    this.isPlaying = false;
    console.log('Audio playback paused');
  }

  /**
   * Stop audio and reset
   */
  stop() {
    if (!this.audioElement) {
      return;
    }

    this.audioElement.pause();
    this.audioElement.currentTime = 0;
    this.isPlaying = false;
    console.log('Audio playback stopped');
  }

  /**
   * Get audio element
   * @returns {HTMLAudioElement}
   */
  getAudioElement() {
    return this.audioElement;
  }

  /**
   * Check if audio is playing
   * @returns {boolean}
   */
  getIsPlaying() {
    return this.isPlaying;
  }

  /**
   * Set volume
   * @param {number} volume - Volume (0.0 to 1.0)
   */
  setVolume(volume) {
    if (this.audioElement) {
      this.audioElement.volume = Math.max(0, Math.min(1, volume));
    }
  }

  /**
   * Mute/unmute audio
   * @param {boolean} muted - True to mute
   */
  setMuted(muted) {
    if (this.audioElement) {
      this.audioElement.muted = muted;
      console.log(`🔊 Audio muted: ${muted}`);
    }
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    this.stop();

    // Revoke object URL
    if (this.objectURL) {
      URL.revokeObjectURL(this.objectURL);
      this.objectURL = null;
    }

    // Clear audio source
    if (this.audioElement) {
      this.audioElement.src = '';
      this.audioElement = null;
    }

    this.currentAudioId = null;
    console.log('AudioController cleaned up');
  }
}
